import React, { useState } from "react";
import {
  Platform,
  View,
  TouchableOpacity,
  StyleSheet,
  type StyleProp,
  type ViewStyle,
  type TextStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { ThemedButton } from "@/components/ThemedButton";
import { MediaCaptureModal } from "@/components/media/MediaCaptureModal";
import {
  type ICapturedGeoLocation,
  getHighAccuracyLocation,
  generateCaptureFileName,
} from "@/services/media-capture";
import { trackMediaCaptured } from "@/services/analytics";
import { CustomAlert } from "@/components/ThemedAlert";
import { useThemeColor } from "@/hooks/useThemeColor";
import * as ImagePicker from "expo-image-picker";

/**
 * Properties for the standalone CaptureVideo component.
 */
export interface ICaptureVideoProps {
  /** Callback triggered when a video is captured */
  onCapture: (file: File | any, location?: ICapturedGeoLocation | null) => Promise<void> | void;
  /** Whether the capture action is disabled */
  disabled?: boolean;
  /** Whether an upload is currently processing */
  uploading?: boolean;
  /** Button title label (defaults to "Video") */
  title?: string;
  /** Button style variant */
  variant?: "primary" | "secondary" | "minimal" | "danger";
  /** Optional container style override */
  style?: StyleProp<ViewStyle>;
  /** Optional text style override */
  textStyle?: StyleProp<TextStyle>;
  /** Initial camera facing type (defaults to "back") */
  initialCameraType?: "back" | "front";
  /** Whether to render an inline camera flip toggle button beside the trigger */
  showFlipButton?: boolean;
  /** Custom trigger render function */
  renderTrigger?: (
    onPress: () => void,
    disabled: boolean,
    toggleCameraType?: () => void,
    cameraType?: "back" | "front"
  ) => React.ReactNode;
}

/**
 * Standalone Video Capture component supporting high-accuracy geolocation tagging,
 * camera hardware permissions, web video recorder modal, native video camera integration,
 * and front/rear camera orientation selection.
 *
 * @param {ICaptureVideoProps} props - Capture video properties.
 * @returns {React.JSX.Element} The rendered CaptureVideo component.
 */
export function CaptureVideo({
  onCapture,
  disabled = false,
  uploading = false,
  title = "Video",
  variant = "secondary",
  style,
  textStyle,
  initialCameraType = "back",
  showFlipButton = false,
  renderTrigger,
}: ICaptureVideoProps): React.JSX.Element {
  const [modalVisible, setModalVisible] = useState(false);
  const [cameraType, setCameraType] = useState<"back" | "front">(initialCameraType);
  const tintColor = useThemeColor({}, "tint");
  const headerBgColor = useThemeColor({}, "buttonSecondary");
  const borderColor = useThemeColor({}, "border");

  const isDisabled = disabled || uploading;

  /**
   * Toggles camera facing mode between front and rear.
   */
  const toggleCameraType = (): void => {
    if (isDisabled) return;
    setCameraType((prev) => (prev === "back" ? "front" : "back"));
  };

  /**
   * Triggers video capture across platforms.
   */
  const handleTrigger = async (): Promise<void> => {
    if (isDisabled) return;

    if (Platform.OS === "web") {
      setModalVisible(true);
      return;
    }

    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== ImagePicker.PermissionStatus.GRANTED && (status as any) !== "granted") {
        CustomAlert.alert("Permission Required", "Please grant camera permission to record video.");
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ["videos"],
        videoQuality: ImagePicker.UIImagePickerControllerQualityType.High,
        cameraType: cameraType === "front" ? ImagePicker.CameraType.front : ImagePicker.CameraType.back,
      });

      if (result.canceled || !result.assets || result.assets.length === 0) {
        return;
      }

      const asset = result.assets[0];
      const location = await getHighAccuracyLocation();
      trackMediaCaptured("video", undefined, Boolean(location));
      const fileName = asset.fileName || generateCaptureFileName("video", asset.mimeType || "video/mp4");
      const fileItem = {
        name: fileName,
        type: asset.mimeType || "video/mp4",
        size: asset.fileSize || 0,
        uri: asset.uri,
      };

      await onCapture(fileItem as any, location);
    } catch (err: any) {
      console.error("[CaptureVideo] Native video capture error:", err);
      CustomAlert.alert("Capture Error", err.message || "Failed to record video.");
    }
  };

  const handleCaptureComplete = (file: File | any, location?: ICapturedGeoLocation | null): void => {
    trackMediaCaptured("video", undefined, Boolean(location));
    setModalVisible(false);
    onCapture(file, location);
  };

  return (
    <>
      {renderTrigger ? (
        renderTrigger(handleTrigger, isDisabled, toggleCameraType, cameraType)
      ) : showFlipButton ? (
        <View style={styles.controlGroup}>
          <ThemedButton
            title={title}
            icon={<Ionicons name="videocam-outline" size={16} color={tintColor} />}
            variant={variant}
            disabled={isDisabled}
            onPress={handleTrigger}
            style={[
              { backgroundColor: headerBgColor, borderColor },
              style,
              styles.mainButton,
            ]}
            textStyle={textStyle}
            accessibilityLabel={`Record Video (${cameraType === "front" ? "Front Camera" : "Rear Camera"})`}
            accessibilityRole="button"
          />
          <TouchableOpacity
            onPress={toggleCameraType}
            disabled={isDisabled}
            style={[
              styles.flipButton,
              { backgroundColor: headerBgColor, borderColor },
              isDisabled && styles.disabledButton,
            ]}
            accessibilityLabel="Turn camera around"
            accessibilityRole="button"
            accessibilityHint={`Toggle camera orientation (currently ${cameraType})`}
          >
            <Ionicons name="camera-reverse-outline" size={16} color={tintColor} />
          </TouchableOpacity>
        </View>
      ) : (
        <ThemedButton
          title={title}
          icon={<Ionicons name="videocam-outline" size={16} color={tintColor} />}
          variant={variant}
          disabled={isDisabled}
          onPress={handleTrigger}
          style={[
            { backgroundColor: headerBgColor, borderColor },
            style,
          ]}
          textStyle={textStyle}
          accessibilityLabel={`Record Video (${cameraType === "front" ? "Front Camera" : "Rear Camera"})`}
          accessibilityRole="button"
        />
      )}

      {/* Web Video Recorder Modal with Camera Flip Support */}
      <MediaCaptureModal
        mode={modalVisible ? "video" : null}
        initialFacingMode={cameraType === "front" ? "user" : "environment"}
        onClose={() => setModalVisible(false)}
        onCaptureComplete={handleCaptureComplete}
      />
    </>
  );
}

export default CaptureVideo;

const styles = StyleSheet.create({
  controlGroup: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  mainButton: {
    borderRightWidth: 0,
  },
  flipButton: {
    paddingHorizontal: 8,
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderRadius: 0,
    minHeight: 38,
  },
  disabledButton: {
    opacity: 0.5,
  },
});
